import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Forma } from 'forma-embedded-view-sdk/auto';
import { addEdgesToMesh, mergeBuildingEdges } from './visualization';
import { buildSceneBVH, enhanceMaterialForRayTracing } from './rayTracing';
import { acceleratedRaycast } from 'three-mesh-bvh';
import { Footprint } from 'forma-embedded-view-sdk/dist/internal/geometry';
import {
	loadAllBuildingWindows,
	originalGeometries,
	rebuildBuildingWithWindows,
} from './buildingWindows';
import proj4 from 'proj4';
import { generateMapboxUrl } from './mapbox';
import { useThreeSceneStore } from '@/store/threeSceneStore';

export const getSceneWithTerrain = async (
	enableExtrudedWalls: boolean = false
): Promise<THREE.Scene> => {
	THREE.Mesh.prototype.raycast = acceleratedRaycast;
	useThreeSceneStore.getState().setHasExtrudedWalls(enableExtrudedWalls);
	const allCategories = ['building', 'vegetation', 'infrastructure', 'furniture', 'road'];
	const colorForCategory: { [key: string]: number } = {
		building: 0xdddddd,
		vegetation: 0x88cc88,
		infrastructure: 0x8888ff,
		furniture: 0xff8888,
		site_limit: 0xffff88,
		road: 0xaaaaaa,
	};

	const terrainScene = new THREE.Scene();
	let globalMinZ = Infinity;
	const allGeometries: { geometry: THREE.BufferGeometry; mesh: THREE.Mesh }[] = [];

	const terrainPath = await Forma.geometry.getPathsByCategory({ category: 'terrain' });

	if (terrainPath.length > 0) {
		const terrainVertexPositions = await Forma.geometry.getTriangles({
			path: terrainPath[0],
		});
		const terrainGeometry = new THREE.BufferGeometry();
		terrainGeometry.setAttribute(
			'position',
			new THREE.BufferAttribute(terrainVertexPositions, 3)
		);
		terrainGeometry.computeVertexNormals();
		terrainGeometry.computeBoundsTree();

		// Find minimum Z value in terrain
		for (let i = 2; i < terrainVertexPositions.length; i += 3) {
			globalMinZ = Math.min(globalMinZ, terrainVertexPositions[i]);
		}

		const terrainMesh = new THREE.Mesh(
			terrainGeometry,
			new THREE.MeshPhysicalMaterial({
				color: 0xf5f5f5,
				side: THREE.DoubleSide,
				roughness: 0.2,
				metalness: 0.0,
				reflectivity: 0.2,
				clearcoat: 0.1,
				clearcoatRoughness: 0.7,
			})
		);

		terrainMesh.receiveShadow = true;
		terrainMesh.castShadow = true;
		terrainMesh.userData.category = 'terrain';
		terrainMesh.userData.isSolid = true;
		terrainMesh.name = 'terrain';
		terrainScene.add(terrainMesh);
		allGeometries.push({ geometry: terrainGeometry, mesh: terrainMesh });
	}

	// Get all other categories
	for (const category of allCategories) {
		const categoryPaths: string[] = await Forma.geometry.getPathsByCategory({ category });

		for (let i = 0; i < categoryPaths.length; i++) {
			const path = categoryPaths[i];
			try {
				const triangles = await Forma.geometry.getTriangles({ path });

				if (triangles && triangles.length > 0) {
					const buildingGeometry = new THREE.BufferGeometry();
					buildingGeometry.setAttribute(
						'position',
						new THREE.BufferAttribute(triangles, 3)
					);

					// Find minimum Z value in this geometry
					for (let i = 2; i < triangles.length; i += 3) {
						globalMinZ = Math.min(globalMinZ, triangles[i]);
					}

					buildingGeometry.computeVertexNormals();
					buildingGeometry.computeBoundsTree();
					buildingGeometry.normalizeNormals();

					// Create material based on category
					let material: THREE.Material;

					if (category === 'building') {
						const baseColor = new THREE.Color(colorForCategory[category] || 0xffffff);
						material = new THREE.MeshPhysicalMaterial({
							color: baseColor,
							side: THREE.DoubleSide,
							metalness: 0.05,
							roughness: 0.2,
							reflectivity: 0.5,
							clearcoat: 0.1,
							clearcoatRoughness: 0.05,
							depthFunc: THREE.LessEqualDepth,
							thickness: 1,
						});
					} else if (category === 'vegetation') {
						material = new THREE.MeshPhysicalMaterial({
							color: colorForCategory[category] || 0xffffff,
							side: THREE.DoubleSide,
							opacity: 0.9,
							transparent: true,
							roughness: 0.8,
							metalness: 0.0,
							depthFunc: THREE.LessEqualDepth,
						});
					} else if (category === 'road') {
						material = new THREE.MeshPhysicalMaterial({
							color: colorForCategory[category] || 0xffffff,
							side: THREE.DoubleSide,
							roughness: 0.4,
							metalness: 0.05,
							reflectivity: 0.6,
							clearcoat: 0.2,
							clearcoatRoughness: 0.3,
							depthFunc: THREE.LessEqualDepth,
							thickness: 1,
						});
					} else {
						material = new THREE.MeshPhysicalMaterial({
							color: colorForCategory[category] || 0xffffff,
							side: THREE.DoubleSide,
							roughness: 0.7,
							metalness: 0.0,
							reflectivity: 0.2,
							depthFunc: THREE.LessEqualDepth,
							thickness: 1,
						});
					}

					const enhancedMaterial = enhanceMaterialForRayTracing(material);

					if (category === 'building' && enableExtrudedWalls) {
						const wallThickness = 0.2;

						// Clone geometries for inner and outer walls
						const innerGeometry = buildingGeometry.clone();
						const outerGeometry = buildingGeometry.clone();

						// Offset INNER geometry INWARD along normals
						const innerPositions = innerGeometry.attributes
							.position as THREE.BufferAttribute;
						const innerNormals = innerGeometry.attributes
							.normal as THREE.BufferAttribute;

						for (let i = 0; i < innerPositions.count; i++) {
							const nx = innerNormals.getX(i);
							const ny = innerNormals.getY(i);
							const nz = innerNormals.getZ(i);
							const expansionFactor = 1.02;
							innerPositions.setXYZ(
								i,
								innerPositions.getX(i) - nx * wallThickness * expansionFactor,
								innerPositions.getY(i) - ny * wallThickness * expansionFactor,
								innerPositions.getZ(i) - nz * wallThickness * expansionFactor
							);
						}

						innerPositions.needsUpdate = true;
						innerGeometry.computeVertexNormals();
						innerGeometry.computeBoundsTree();

						// Outer geometry remains at original position
						outerGeometry.computeVertexNormals();
						outerGeometry.computeBoundsTree();

						// Create cap geometry
						const outerIndex = outerGeometry.index;
						if (!outerIndex) {
							console.warn('Building geometry has no index, skipping cap generation');
							continue;
						}

						const innerPosArray = (
							innerGeometry.attributes.position as THREE.BufferAttribute
						).array as Float32Array;
						const outerPosArray = (
							outerGeometry.attributes.position as THREE.BufferAttribute
						).array as Float32Array;

						const capVertices: number[] = [];
						const capIndices: number[] = [];

						// Find boundary edges
						const edgeCount = new Map<string, number>();

						for (let i = 0; i < outerIndex.count; i += 3) {
							const a = outerIndex.getX(i);
							const b = outerIndex.getX(i + 1);
							const c = outerIndex.getX(i + 2);

							const edges = [
								[a, b],
								[b, c],
								[c, a],
							];

							for (const [v1, v2] of edges) {
								const edgeKey = `${Math.min(v1, v2)}_${Math.max(v1, v2)}`;
								edgeCount.set(edgeKey, (edgeCount.get(edgeKey) || 0) + 1);
							}
						}

						// Only create caps for boundary edges
						for (const [edgeKey, count] of edgeCount.entries()) {
							if (count !== 1) continue;

							const [v1Str, v2Str] = edgeKey.split('_');
							const v1 = parseInt(v1Str);
							const v2 = parseInt(v2Str);

							const innerV1 = [
								innerPosArray[v1 * 3],
								innerPosArray[v1 * 3 + 1],
								innerPosArray[v1 * 3 + 2],
							];
							const innerV2 = [
								innerPosArray[v2 * 3],
								innerPosArray[v2 * 3 + 1],
								innerPosArray[v2 * 3 + 2],
							];

							const outerV1 = [
								outerPosArray[v1 * 3],
								outerPosArray[v1 * 3 + 1],
								outerPosArray[v1 * 3 + 2],
							];
							const outerV2 = [
								outerPosArray[v2 * 3],
								outerPosArray[v2 * 3 + 1],
								outerPosArray[v2 * 3 + 2],
							];

							const baseIdx = capVertices.length / 3;
							capVertices.push(...innerV1, ...outerV1, ...innerV2, ...outerV2);

							capIndices.push(
								baseIdx,
								baseIdx + 1,
								baseIdx + 2,
								baseIdx + 2,
								baseIdx + 1,
								baseIdx + 3
							);
						}

						const mergedGeometry = new THREE.BufferGeometry();

						const allVertices: number[] = [];
						const allIndices: number[] = [];

						// Add outer surface
						const outerPosAttr = outerGeometry.attributes
							.position as THREE.BufferAttribute;
						for (let i = 0; i < outerPosAttr.count; i++) {
							allVertices.push(
								outerPosAttr.getX(i),
								outerPosAttr.getY(i),
								outerPosAttr.getZ(i)
							);
						}
						const outerIndexArray = outerGeometry.index!.array;
						for (let i = 0; i < outerIndexArray.length; i += 3) {
							allIndices.push(
								outerIndexArray[i],
								outerIndexArray[i + 1],
								outerIndexArray[i + 2]
							);
						}

						// Add inner surface with reversed winding
						const innerVertexOffset = allVertices.length / 3;
						const innerPosAttr = innerGeometry.attributes
							.position as THREE.BufferAttribute;
						for (let i = 0; i < innerPosAttr.count; i++) {
							allVertices.push(
								innerPosAttr.getX(i),
								innerPosAttr.getY(i),
								innerPosAttr.getZ(i)
							);
						}
						const innerIndexArray = innerGeometry.index!.array;
						for (let i = innerIndexArray.length - 3; i >= 0; i -= 3) {
							allIndices.push(
								innerIndexArray[i] + innerVertexOffset,
								innerIndexArray[i + 2] + innerVertexOffset,
								innerIndexArray[i + 1] + innerVertexOffset
							);
						}

						// Add cap faces
						const capVertexOffset = allVertices.length / 3;
						allVertices.push(...capVertices);
						for (let i = 0; i < capIndices.length; i++) {
							allIndices.push(capIndices[i] + capVertexOffset);
						}

						mergedGeometry.setAttribute(
							'position',
							new THREE.BufferAttribute(new Float32Array(allVertices), 3)
						);
						mergedGeometry.setIndex(allIndices);
						mergedGeometry.computeVertexNormals();
						mergedGeometry.computeBoundsTree();

						const buildingMaterial = enhancedMaterial.clone();
						buildingMaterial.side = THREE.DoubleSide;

						const buildingMesh = new THREE.Mesh(mergedGeometry, buildingMaterial);

						// FIX: Explicitly set category to 'building', not the loop variable
						buildingMesh.userData = {
							category: 'building', // Hardcode 'building' here
							isSolid: true,
							path,
						};
						buildingMesh.castShadow = true;
						buildingMesh.receiveShadow = true;
						buildingMesh.name = `building_${path}`; // Add descriptive name

						terrainScene.add(buildingMesh);
						allGeometries.push({ geometry: mergedGeometry, mesh: buildingMesh });

						// Add edges for building
						addEdgesToMesh(buildingMesh, terrainScene);
					} else if (category === 'building' && !enableExtrudedWalls) {
						// Building without extrusion (simple flat surfaces)
						const buildingMesh = new THREE.Mesh(buildingGeometry, enhancedMaterial);

						buildingMesh.userData = {
							category: 'building',
							isSolid: true,
							path,
						};
						buildingMesh.castShadow = true;
						buildingMesh.receiveShadow = true;
						buildingMesh.name = `building_${path}`;

						terrainScene.add(buildingMesh);
						allGeometries.push({ geometry: buildingGeometry, mesh: buildingMesh });

						// Add edges for building
						addEdgesToMesh(buildingMesh, terrainScene);
					} else {
						// For other categories (vegetation, infrastructure, etc.)
						const buildingMesh = new THREE.Mesh(buildingGeometry, enhancedMaterial);

						// FIX: Use the actual category from the loop
						buildingMesh.userData = {
							category: category, // This will be 'vegetation', 'infrastructure', etc.
							isSolid: true,
							path,
						};
						buildingMesh.castShadow = true;
						buildingMesh.receiveShadow = true;
						buildingMesh.name = `${category}_${path}`; // Add descriptive name

						terrainScene.add(buildingMesh);
						allGeometries.push({ geometry: buildingGeometry, mesh: buildingMesh });
					}
				}
			} catch (error) {
				console.error(`Error processing ${category} ${i}:`, error);
			}
		}
	}

	// Normalize all geometries
	if (globalMinZ !== Infinity && globalMinZ !== 0) {
		const zOffset = -globalMinZ;
		allGeometries.forEach(({ geometry }) => {
			const positions = geometry.attributes.position as THREE.BufferAttribute;
			const positionArray = positions.array as Float32Array;
			for (let i = 2; i < positionArray.length; i += 3) {
				positionArray[i] += zOffset;
			}
			positions.needsUpdate = true;
			geometry.computeBoundingBox();
			geometry.computeBoundingSphere();
			geometry.computeBoundsTree();
		});
	}

	terrainScene.matrixWorldNeedsUpdate = true;

	// Load and rebuild windows for buildings
	await loadAllBuildingWindows(allGeometries);

	// Rebuild buildings with windows
	const buildingMeshes = allGeometries
		.filter(({ mesh }) => mesh.userData.category === 'building')
		.map(({ mesh }) => mesh);

	for (const buildingMesh of buildingMeshes) {
		originalGeometries.set(buildingMesh, buildingMesh.geometry.clone());
		await rebuildBuildingWithWindows(buildingMesh, terrainScene);
	}

	const drawRoad = (points: THREE.Vector3[], meshName: string, userData: object) => {
		if (points.length < 2) {
			return;
		}

		const roadWidth = 5;
		const halfWidth = roadWidth / 2;
		const zOffset = 0.05;

		const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);
		const sampleCount = Math.max(points.length * 12, 24);
		const samples = curve.getSpacedPoints(sampleCount - 1);

		const positions = new Float32Array(sampleCount * 2 * 3);
		const indices: number[] = [];

		const up = new THREE.Vector3(0, 0, 1);
		const tangent = new THREE.Vector3();
		const normal = new THREE.Vector3();

		for (let i = 0; i < sampleCount; i++) {
			const curr = samples[i].clone();
			curr.z += zOffset;

			const next = samples[Math.min(i + 1, sampleCount - 1)];
			tangent.copy(next).sub(curr).normalize();

			if (tangent.lengthSq() < 1e-12) {
				const prev = samples[Math.max(i - 1, 0)];
				tangent.copy(curr).sub(prev).normalize();
			}

			normal.crossVectors(up, tangent).normalize().multiplyScalar(halfWidth);

			const left = curr.clone().add(normal);
			const right = curr.clone().sub(normal);

			const base = i * 6;
			positions[base] = left.x;
			positions[base + 1] = left.y;
			positions[base + 2] = left.z;
			positions[base + 3] = right.x;
			positions[base + 4] = right.y;
			positions[base + 5] = right.z;
		}

		for (let i = 0; i < sampleCount - 1; i++) {
			const base = i * 2;
			indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
		}

		const ribbonGeometry = new THREE.BufferGeometry();
		ribbonGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
		ribbonGeometry.setIndex(indices);
		ribbonGeometry.computeVertexNormals();

		const ribbonMaterial = new THREE.MeshPhysicalMaterial({
			color: 0x595959,
			side: THREE.DoubleSide,
			roughness: 0.5,
			metalness: 0.1,
		});
		ribbonMaterial.polygonOffset = true;
		ribbonMaterial.polygonOffsetFactor = 0;
		ribbonMaterial.polygonOffsetUnits = -4;
		const ribbonMesh = new THREE.Mesh(ribbonGeometry, ribbonMaterial);
		ribbonMesh.name = meshName + '_Ribbon';
		ribbonMesh.userData = userData;
		ribbonMesh.userData.category = 'road';
		terrainScene.add(ribbonMesh);
	};

	type PolygonUserData = {
		isRoad?: boolean;
		isSiteLimit?: boolean;
		isEdgeWireframe?: boolean;
		[key: string]: unknown;
	};

	const drawPolygon = (
		polygon: Footprint,
		meshName: string,
		color: THREE.ColorRepresentation,
		userData: PolygonUserData
	) => {
		const coordinates = polygon.coordinates;
		if (coordinates && coordinates.length > 0) {
			const raycaster = new THREE.Raycaster();
			const downDirection = new THREE.Vector3(0, 0, -1); // Z-up: raycast downward

			// Get terrain mesh for raycasting
			const terrainMeshes = allGeometries
				.filter(({ mesh }) => mesh.userData.category === 'terrain')
				.map(({ mesh }) => mesh);

			const getTerrainHeight = (x: number, y: number): number => {
				const rayOrigin = new THREE.Vector3(x, y, 1000);
				raycaster.set(rayOrigin, downDirection);
				const intersects = raycaster.intersectObjects(terrainMeshes, false);

				if (intersects.length > 0) {
					return intersects[0].point.z;
				}
				return 0;
			};

			const densifiedPoints: THREE.Vector3[] = [];
			const segmentDensity = 0.5; // Add point every 0.5 units

			const isClosed = !userData.isRoad;
			const segmentCount = isClosed ? coordinates.length : coordinates.length - 1;

			for (let i = 0; i < segmentCount; i++) {
				const [x1, y1] = coordinates[i];
				const [x2, y2] = coordinates[isClosed ? (i + 1) % coordinates.length : i + 1];
				if (x2 === undefined || y2 === undefined) continue; // Skip if out of bounds

				const start = new THREE.Vector2(x1, y1);
				const end = new THREE.Vector2(x2, y2);
				const distance = start.distanceTo(end);

				const numSegments = Math.ceil(distance / segmentDensity);

				for (let j = 0; j < numSegments; j++) {
					const t = j / numSegments;
					const x = x1 + (x2 - x1) * t;
					const y = y1 + (y2 - y1) * t;
					const z = getTerrainHeight(x, y);

					densifiedPoints.push(new THREE.Vector3(x, y, z));
				}
			}

			// Close the loop
			if (densifiedPoints.length > 0 && isClosed) {
				densifiedPoints.push(densifiedPoints[0].clone());
			}

			if (userData.isRoad) {
				drawRoad(densifiedPoints, meshName + '_Ribbon', {
					isRoadMesh: true,
					isSolid: true,
				});
			}
			// Create line geometry from densified points
			const geometry = new THREE.BufferGeometry().setFromPoints(densifiedPoints);

			const line = new THREE.LineBasicMaterial({
				color, // Bright yellow
				linewidth: 5,
				transparent: false,
				depthTest: true,
			});

			const lineMesh = new THREE.Line(geometry, line);
			lineMesh.name = meshName;
			lineMesh.userData = userData;
			lineMesh.renderOrder = 999;

			terrainScene.add(lineMesh);
		}
	};
	const siteLimit = await Forma.geometry.getPathsByCategory({ category: 'site_limit' });
	if (siteLimit.length > 0) {
		const polygon = await Forma.geometry.getFootprint({
			path: siteLimit[0],
		});
		if (polygon) {
			drawPolygon(polygon, 'Site Limit', 0xff0000, {
				isSiteLimit: true,
				isEdgeWireframe: true,
				isSolid: false,
			});
		}
	}

	const roads = await Forma.geometry.getPathsByCategory({ category: 'road' });
	for (const roadPath of roads) {
		const polygon = await Forma.geometry.getFootprint({ path: roadPath });
		if (polygon) {
			drawPolygon(polygon, `Road_${roadPath}`, 0x555555, {
				isRoad: true,
				isEdgeWireframe: true,
				isSolid: false,
				category: 'road',
			});
		}
	}
	buildSceneBVH(terrainScene);
	mergeBuildingEdges(terrainScene);
	mergeNonBuildingCategories(terrainScene);
	return terrainScene;
};

/**
 * Merges all meshes of non-building categories (vegetation, infrastructure, furniture)
 * into single meshes per category to reduce draw calls.
 * Buildings are kept separate since they need window modifications.
 */
function mergeNonBuildingCategories(scene: THREE.Scene): void {
	const categoriesToMerge = ['vegetation', 'infrastructure', 'furniture'];
	
	for (const category of categoriesToMerge) {
		const meshesOfCategory: THREE.Mesh[] = [];
		
		scene.traverse((child) => {
			if (
				child instanceof THREE.Mesh &&
				child.userData.category === category &&
				!child.userData.isMergedCategory
			) {
				meshesOfCategory.push(child);
			}
		});
		
		if (meshesOfCategory.length <= 1) continue; // Skip if 0 or 1 mesh
		
		// Collect geometries with world transforms baked in
		const geoms = meshesOfCategory.map((mesh) => {
			mesh.updateMatrixWorld(true);
			const geo = mesh.geometry.clone();
			geo.applyMatrix4(mesh.matrixWorld);
			return geo;
		});
		
		const merged = mergeGeometries(geoms, false);
		if (!merged) continue;
		
		geoms.forEach(g => g.dispose());
		
		// Create merged mesh
		const firstMaterial = meshesOfCategory[0].material;
		let baseColor = 0x888888;
		if (firstMaterial instanceof THREE.Material && 'color' in firstMaterial) {
			const colorProp = (firstMaterial as THREE.MeshPhysicalMaterial).color;
			if (colorProp) {
				baseColor = colorProp.getHex();
			}
		}
		
		const material = new THREE.MeshPhysicalMaterial({
			color: baseColor,
			side: THREE.FrontSide,
			roughness: 0.5,
			metalness: 0.1,
		});
		
		const mergedMesh = new THREE.Mesh(merged, material);
		mergedMesh.name = `${category}_merged`;
		mergedMesh.userData.category = category;
		mergedMesh.userData.isMergedCategory = true;
		mergedMesh.userData.isSolid = true;
		mergedMesh.castShadow = true;
		mergedMesh.receiveShadow = true;
		
		// Remove original meshes
		for (const mesh of meshesOfCategory) {
			mesh.parent?.remove(mesh);
			mesh.geometry.dispose();
			if (mesh.material instanceof THREE.Material) {
				mesh.material.dispose();
			}
		}
		
		scene.add(mergedMesh);
	}
}

// Register common UTM zones dynamically
const registerUTMZone = (zone: number) => {
	const epsg = `EPSG:326${zone.toString().padStart(2, '0')}`;
	if (!proj4.defs(epsg)) {
		proj4.defs(epsg, `+proj=utm +zone=${zone} +ellps=WGS84 +datum=WGS84 +units=m +no_defs`);
	}
};

// Register zones 1-60 (covers worldwide UTM)
for (let zone = 1; zone <= 60; zone++) {
	registerUTMZone(zone);
}
proj4.defs('EPSG:4326', '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs');

function applyDrapeUVs(geometry: THREE.BufferGeometry, min: THREE.Vector3, max: THREE.Vector3) {
	const posAttribute = geometry.attributes.position;

	if (!geometry.attributes.uv) {
		const uvArray = new Float32Array(posAttribute.count * 2);
		geometry.setAttribute('uv', new THREE.BufferAttribute(uvArray, 2));
	}
	const uvAttribute = geometry.attributes.uv;

	// Z-up: horizontal plane is X-Y, vertical is Z
	const width = max.x - min.x;
	const depth = max.y - min.y;

	const safeW = width || 1;
	const safeD = depth || 1;

	for (let i = 0; i < posAttribute.count; i++) {
		const x = posAttribute.getX(i);
		const y = posAttribute.getY(i);

		const u = (x - min.x) / safeW;
		const v = (y - min.y) / safeD;

		uvAttribute.setXY(i, u, v);
	}

	uvAttribute.needsUpdate = true;
}

export const addTextureToTerrain = async (
	scene: THREE.Scene,
	renderer: THREE.WebGLRenderer
): Promise<boolean> => {
	try {
		const mesh = scene.getObjectByName('terrain') as THREE.Mesh;
		const project = await Forma.project.get();

		const srid = project.srid ? `EPSG:${project.srid}` : 'EPSG:32632';
		const refX = project.refPoint ? project.refPoint[0] : 0;
		const refY = project.refPoint ? project.refPoint[1] : 0;

		if (!mesh?.geometry) return false;

		mesh.geometry.computeBoundingBox();
		const { min, max } = mesh.geometry.boundingBox!;

		// Z-up: use X-Y for horizontal extent
		const mapWidth = max.x - min.x;
		const mapDepth = max.y - min.y;

		// Transform horizontal coordinates (X, Y) to lat/lon
		const [minLon, minLat] = proj4(srid, 'EPSG:4326', [refX + min.x, refY + min.y]);
		const [maxLon, maxLat] = proj4(srid, 'EPSG:4326', [refX + max.x, refY + max.y]);

		const res = 1280;
		const imgW = mapWidth > mapDepth ? res : Math.round(res * (mapWidth / mapDepth));
		const imgH = mapWidth > mapDepth ? Math.round(res * (mapDepth / mapWidth)) : res;
		const renderScale = renderer.getPixelRatio() > 1 ? 2 : 1;
		const url = generateMapboxUrl(minLon, minLat, maxLon, maxLat, imgW, imgH, renderScale);

		let texture = null;
		try {
			const loader = new THREE.TextureLoader();
			texture = await loader.loadAsync(url);

			texture.colorSpace = THREE.SRGBColorSpace;

			// Use maximum anisotropic filtering supported by GPU
			texture.anisotropy = renderer.capabilities.getMaxAnisotropy();

			// Use better filtering
			texture.minFilter = THREE.LinearMipmapLinearFilter;
			texture.magFilter = THREE.LinearFilter;
			texture.generateMipmaps = true;
		} catch (e) {
			console.warn('Texture load failed:', e);
		}

		applyDrapeUVs(mesh.geometry, min, max);

		mesh.userData.getColorAt = (uv: THREE.Vector2): string => {
			if (!texture || !texture.image) return '#000000';

			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) return '#000000';

			canvas.width = texture.image.width;
			canvas.height = texture.image.height;
			ctx.drawImage(texture.image, 0, 0);

			const x = Math.floor(uv.x * texture.image.width);
			const y = Math.floor((1 - uv.y) * texture.image.height);
			const pixel = ctx.getImageData(x, y, 1, 1).data;

			return `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
		};

		mesh.material = new THREE.MeshStandardMaterial({
			color: 0xffffff,
			map: texture,
			side: THREE.DoubleSide,
		});
		mesh.material.needsUpdate = true;

		return true;
	} catch (err) {
		console.error('addTextureToTerrain error:', err);
		return false;
	}
};
